import { useState, useEffect, useRef, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { 
  Play, Square, RefreshCw, Terminal, CheckCircle, 
  XCircle, AlertTriangle, Key, ChevronDown, ChevronUp,
  Lock, ChevronRight
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { streamPasswordUpdate } from '@/services/api'

/**
 * Password Update Wizard with Real-Time Terminal
 * 
 * Features:
 * - Real-time password update streaming via SSE
 * - Live terminal output streaming
 * - Progress tracking per account
 * - Success/failure status for each password update
 * - Ability to cancel/retry
 */
export function PasswordUpdateWizard({
  jobId,
  server,
  accounts = [],
  onComplete,
  onCancel,
  onContinue,
  className
}) {
  // State
  const [isExecuting, setIsExecuting] = useState(false)
  const [showTerminal, setShowTerminal] = useState(true)
  const [lines, setLines] = useState([])
  const [progress, setProgress] = useState({
    current: 0,
    total: accounts.length,
    success: 0,
    failed: 0,
    pending: accounts.length
  })
  const [accountStatuses, setAccountStatuses] = useState({})
  const [error, setError] = useState(null)
  const [isComplete, setIsComplete] = useState(false)
  const [completionData, setCompletionData] = useState(null)
  
  const scrollRef = useRef(null)
  const streamRef = useRef(null)
  const outputEndRef = useRef(null)
  const serverErrorReceived = useRef(false)

  // Auto-scroll to bottom
  useEffect(() => {
    if (outputEndRef.current) {
      outputEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [lines])

  // Initialize account statuses
  useEffect(() => {
    const statuses = {}
    accounts.forEach(acc => {
      statuses[acc.email] = { status: 'pending', output: [] }
    })
    setAccountStatuses(statuses)
    setProgress(prev => ({ ...prev, total: accounts.length, pending: accounts.length }))
  }, [accounts])

  const addLine = (type, content, metadata = {}) => {
    setLines(prev => [...prev, {
      type,
      content: typeof content === 'string' ? content : JSON.stringify(content),
      timestamp: new Date().toLocaleTimeString(),
      id: `${Date.now()}-${Math.random()}`,
      ...metadata
    }])
  }

  const handleStreamMessage = useCallback((type, data) => {
    switch (type) {
      case 'init':
        addLine('info', '🔐 Starting password update process...')
        addLine('info', `📧 Total accounts: ${accounts.length}`)
        addLine('info', `🖥️  Target server: ${server?.name} (${server?.host})`)
        break

      case 'progress':
        setProgress(prev => ({
          ...prev,
          current: data.current,
          total: data.total
        }))
        addLine('command', `[${data.current}/${data.total}] Updating password: ${data.email}`, { email: data.email })
        
        // Update account status
        setAccountStatuses(prev => ({
          ...prev,
          [data.email]: { ...prev[data.email], status: 'updating', output: [] }
        }))
        break

      case 'command':
        addLine('info', `Executing: ${data.replace(/'[^']*'/g, "'***'")}`)
        break

      case 'output':
        if (typeof data === 'string') {
          addLine('stdout', data)
        } else if (data.email && data.line) {
          addLine('stdout', `  ${data.line}`, { email: data.email })
          setAccountStatuses(prev => ({
            ...prev,
            [data.email]: {
              ...prev[data.email],
              output: [...(prev[data.email]?.output || []), data.line]
            }
          }))
        }
        break

      case 'error':
        serverErrorReceived.current = true
        const errorMsg = typeof data === 'string' ? data : (data.line || data.message || JSON.stringify(data))
        addLine('stderr', `  ✗ ${errorMsg}`, { email: data?.email })
        break

      case 'success':
        addLine('success', `  ✓ ${data.email}: ${data.message}`, { email: data.email })
        setProgress(prev => ({ ...prev, success: prev.success + 1, pending: prev.pending - 1 }))
        setAccountStatuses(prev => ({
          ...prev,
          [data.email]: { ...prev[data.email], status: 'success', message: data.message }
        }))
        break

      case 'failed':
        addLine('error', `  ✗ ${data.email}: ${data.message}`, { email: data.email })
        setProgress(prev => ({ ...prev, failed: prev.failed + 1, pending: prev.pending - 1 }))
        setAccountStatuses(prev => ({
          ...prev,
          [data.email]: { ...prev[data.email], status: 'failed', message: data.message }
        }))
        break

      case 'complete':
        const allFailed = data.failed === data.total
        if (allFailed) {
          addLine('error', `\n❌ Password update failed! All ${data.total} accounts failed.`)
        } else if (data.failed > 0) {
          addLine('warning', `\n⚠️ Password update completed with issues: ${data.success} success, ${data.failed} failed`)
        } else {
          addLine('success', `\n✅ Password update completed! All ${data.success} accounts updated successfully.`)
        }
        addLine('info', `📊 Summary: ${data.success} success, ${data.failed} failed`)
        setCompletionData(data)
        setIsComplete(true)
        onComplete?.(data)
        break

      default:
        addLine('raw', data)
    }
  }, [accounts.length, onComplete])

  const startPasswordUpdate = useCallback(async () => {
    if (!jobId || isExecuting) return
    serverErrorReceived.current = false
    setIsExecuting(true)
    setError(null)
    setLines([])
    setIsComplete(false)
    setCompletionData(null)
    addLine('system', 'Initializing password update stream...')

    try {
      console.log('Starting password update stream for job:', jobId)
      streamRef.current = streamPasswordUpdate(jobId, {
        onMessage: ({ type, data }) => {
          console.log('Password update stream message:', type, data)
          handleStreamMessage(type, data)
        },
        onError: (err) => {
          console.error('Password update stream error:', err)
          // Don't show connection error if we already received an error from server
          if (!serverErrorReceived.current) {
            const errorMsg = err?.message || err?.type || 'Connection failed'
            setError(errorMsg)
            addLine('error', '✗ ' + errorMsg)
          }
          setIsExecuting(false)
        },
        onComplete: () => {
          console.log('Password update stream completed')
          setIsExecuting(false)
        }
      })
    } catch (err) {
      console.error('Failed to start password update:', err)
      const errorMsg = err?.message || String(err)
      setError(errorMsg)
      addLine('error', 'Failed to start: ' + errorMsg)
      setIsExecuting(false)
    }
  }, [jobId, isExecuting, handleStreamMessage])

  const cancelExecution = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.close()
      addLine('warning', '⏹️ Password update cancelled')
      setIsExecuting(false)
      onCancel?.()
    }
  }, [onCancel])

  const getLineColor = (type) => {
    switch (type) {
      case 'command': return 'text-blue-400'
      case 'stdout': return 'text-green-400'
      case 'stderr': return 'text-red-400'
      case 'error': return 'text-red-500'
      case 'success': return 'text-green-500'
      case 'warning': return 'text-yellow-400'
      case 'info': return 'text-cyan-400'
      case 'system': return 'text-purple-400'
      default: return 'text-gray-300'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'failed': return <XCircle className="w-4 h-4 text-red-500" />
      case 'updating': return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />
      default: return <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
    }
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success': return <Badge className="bg-green-500/20 text-green-600">Updated</Badge>
      case 'failed': return <Badge variant="destructive">Failed</Badge>
      case 'updating': return <Badge className="bg-blue-500 animate-pulse">Updating...</Badge>
      default: return <Badge variant="outline">Pending</Badge>
    }
  }

  return (
    <div className={cn("space-y-4", className)}>
      {/* Progress Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              <CardTitle>Password Update</CardTitle>
            </div>
            {isExecuting && <Badge className="text-blue-600 border-blue-600">● Running</Badge>}
            {isComplete && progress.failed === 0 && <Badge className="text-green-600 border-green-600">✓ Complete</Badge>}
            {isComplete && progress.failed > 0 && <Badge className="text-yellow-600 border-yellow-600">⚠ Partial</Badge>}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <Key className="w-4 h-4" />
            <span>{accounts.length} accounts to update</span>
            {server && <span className="text-xs">on source: {server.name}</span>}
          </div>

          {isExecuting && (
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span>Progress</span>
                <span>{progress.current} / {progress.total}</span>
              </div>
              <Progress value={(progress.current / progress.total) * 100} className="h-2" />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span className="text-green-600">✓ {progress.success} success</span>
                <span className="text-red-600">✗ {progress.failed} failed</span>
                <span className="text-gray-500">○ {progress.pending} pending</span>
              </div>
            </div>
          )}

          {isComplete && (
            <div className={`p-4 rounded-lg mb-4 ${progress.failed === 0 ? 'bg-green-50 border border-green-200' : progress.failed === progress.total ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                {progress.failed === 0 ? <CheckCircle className="w-5 h-5 text-green-600" /> :
                 progress.failed === progress.total ? <XCircle className="w-5 h-5 text-red-600" /> :
                 <AlertTriangle className="w-5 h-5 text-yellow-600" />}
                <span className={`font-medium ${progress.failed === 0 ? 'text-green-700' : progress.failed === progress.total ? 'text-red-700' : 'text-yellow-700'}`}>
                  Password Update {progress.failed === 0 ? 'Successful' : progress.failed === progress.total ? 'Failed' : 'Partial'}
                </span>
              </div>
              <div className="text-sm text-muted-foreground">
                <p>Success: {progress.success} | Failed: {progress.failed} | Total: {progress.total}</p>
              </div>
            </div>
          )}

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2 flex-wrap">
            {!isExecuting && !isComplete ? (
              <Button onClick={startPasswordUpdate} disabled={!jobId || accounts.length === 0}>
                <Play className="w-4 h-4 mr-2" />
                Start Password Update
              </Button>
            ) : isExecuting ? (
              <Button variant="destructive" onClick={cancelExecution}>
                <Square className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => { setIsComplete(false); setLines([]); setAccountStatuses({}) }}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry
                </Button>
                {onContinue && (
                  <Button onClick={onContinue}>
                    Continue
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </>
            )}
            
            <Button variant="outline" onClick={() => setShowTerminal(!showTerminal)}>
              <Terminal className="w-4 h-4 mr-2" />
              {showTerminal ? 'Hide' : 'Show'} Terminal
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Terminal Card */}
      {showTerminal && (
        <Card className="bg-black border-gray-800">
          <CardHeader className="py-3 px-4 border-b border-gray-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-400">
                <Terminal className="w-4 h-4" />
                <span className="text-sm font-mono">Password Update Terminal</span>
                {isExecuting && <RefreshCw className="w-3 h-3 animate-spin text-blue-500" />}
              </div>
              {lines.length > 0 && (
                <Button variant="ghost" size="sm" className="h-6 text-xs text-gray-400" onClick={() => setLines([])}>
                  Clear
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-80 px-4 py-3">
              {lines.length === 0 ? (
                <div className="text-gray-500 text-sm font-mono">
                  <div>$ # Password Update Terminal Ready</div>
                  <div>$ # Click "Start Password Update" to begin</div>
                </div>
              ) : (
                <div className="space-y-1 font-mono text-sm">
                  {lines.map((line) => (
                    <div key={line.id} className={cn("break-all", getLineColor(line.type))}>
                      {line.type === 'command' && <span className="text-gray-500">{line.timestamp} </span>}
                      {line.content}
                    </div>
                  ))}
                  {isExecuting && <div className="text-yellow-400 animate-pulse">{'>'} Executing...</div>}
                  <div ref={outputEndRef} />
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Account Status Card */}
      {accounts.length > 0 && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Account Status</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-48">
              <div className="space-y-2">
                {accounts.map((account) => {
                  const status = accountStatuses[account.email]
                  return (
                    <div key={account.email} className="flex items-center justify-between p-2 rounded bg-muted/50">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(status?.status)}
                        <span className="text-sm font-mono">{account.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {status?.message && <span className="text-xs text-muted-foreground truncate max-w-32">{status.message}</span>}
                        {getStatusBadge(status?.status)}
                      </div>
                    </div>
                  )
                })}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default PasswordUpdateWizard
